import * as React from 'react'
import type { CalendarEvent } from '../../typings'

// Tiles open the detail popover by calling into this context rather than having
// an onSelect threaded down through every grid layer (week/day/month → columns
// → tiles). The popover must anchor to the exact tile element, so the opener
// carries the tile's DOM node alongside the event.
export type CalendarSelection = {
  event: CalendarEvent
  anchorEl: HTMLElement
}

type CalendarSelectionContextValue = {
  open: (event: CalendarEvent, anchorEl: HTMLElement) => void
}

const CalendarSelectionContext =
  React.createContext<CalendarSelectionContextValue | null>(null)

export function CalendarSelectionProvider({
  open,
  children,
}: {
  open: (event: CalendarEvent, anchorEl: HTMLElement) => void
  children: React.ReactNode
}) {
  const value = React.useMemo(() => ({ open }), [open])
  return (
    <CalendarSelectionContext.Provider value={value}>
      {children}
    </CalendarSelectionContext.Provider>
  )
}

/**
 * Returns an opener for the detail popover, or a no-op when no provider is
 * mounted — tiles stay renderable in isolation (tests, storybook) without a
 * selection host, and simply do nothing on click.
 */
export function useOpenEventDetail(): (
  event: CalendarEvent,
  anchorEl: HTMLElement,
) => void {
  const ctx = React.useContext(CalendarSelectionContext)
  return ctx?.open ?? noop
}

function noop() {}
